import type { MultiDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { isNil } from 'lodash';
import { createSelector, type Selector } from 'reselect';
import { HashType, getUniqueDataFromCache, isDataLoaded } from 'src/services/pivotServiceCache';
import type { AppState } from 'src/reducers';
import { ViewDataState } from 'src/types/Domain';
import type { Pivot, TreePivot } from 'src/worker/pivotWorker.types';

//#region PricingOverTime component selectors

const selectCompanionViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.pricing.viewDataStateCompanion;
const selectMassEditViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.pricing.viewDataStateMassEdit;
const selectOverTimeViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.pricing.viewDataStateOverTime;
const selectWorklistLiveData: Selector<AppState, Pivot> = (state) => state.worklist.liveData;
const selectMassEditData: Selector<AppState, TreePivot> = (state) =>
  state.pages.assortmentBuild.pricing.massEditData?.tree ?? [];
export const selectPricingCompanionData: Selector<AppState, TreePivot> = (state) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.pricing, HashType.pricingCompanion)?.tree ?? [];
};
export const selectPricingOverTimeData: Selector<AppState, TreePivot> = (state) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.pricing, HashType.pricingOverTime)?.tree ?? [];
};

/** Determines if a viewdefn value was configured */
export const selectPricingHasMassEditViewDefn: Selector<AppState, boolean, [MultiDefnProps]> = (_state, defns) =>
  defns.view.length >= 6;
export const selectPricingHasMassEditModel: Selector<AppState, boolean, [string | undefined]> = (
  _state,
  massEditModel,
) => !isNil(massEditModel);

interface PricingMassEditInputs {
  defns: MultiDefnProps;
  massEditModel: string | undefined;
}

interface PricingMassEditOutputs {
  hasMassEditModel: boolean;
  hasMassEditViewDefn: boolean;
}

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectPricingMassEditInputs: Selector<AppState, PricingMassEditOutputs, [PricingMassEditInputs]> = (
  _state,
  inputs,
) => {
  const { defns, massEditModel } = inputs;
  return {
    hasMassEditModel: selectPricingHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectPricingHasMassEditViewDefn(_state, defns),
  };
};

/**
 * Retrieve the viewDataStates to be included in the view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectPricingViewDataStates = createSelector(
  selectCompanionViewDataState,
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditViewDefn,
  (viewDataStateCompanion, viewDataStateMassEdit, viewDataStateOverTime, hasMassEdit) => {
    const viewDataStates = hasMassEdit
      ? [viewDataStateCompanion, viewDataStateOverTime, viewDataStateMassEdit]
      : [viewDataStateCompanion, viewDataStateOverTime];
    return viewDataStates;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectPricingMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectPricingMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

//#endregion

//#region TabbedPricingGrid component selectors

export const selectTabbedPricingHasMassEditViewDefn: Selector<AppState, boolean, [string[]]> = (_state, viewDefns) =>
  viewDefns.length >= 2;

/**
 * Retrieve massEdit data.
 *
 * For legacy reasons when no massEdit data fetch occurs, the worklist data is returned by default.
 * */
export const selectTabbedPricingMassEditData = createSelector(
  selectWorklistLiveData,
  selectMassEditData,
  selectPricingHasMassEditModel,
  (worklistData, massEditData, hasMassEditModel) => {
    return hasMassEditModel ? massEditData : worklistData.tree;
  },
);

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectTabbedPricingMassEditInputs: Selector<
  AppState,
  PricingMassEditOutputs,
  [string | undefined, string[]]
> = (_state, massEditModel, viewDefns) => {
  return {
    hasMassEditModel: selectPricingHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectTabbedPricingHasMassEditViewDefn(_state, viewDefns),
  };
};

/**
 * Retrieve tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const massEditLoaded = isDataLoaded(viewDataStateMassEdit);
    const overTimeLoaded = isDataLoaded(viewDataStateOverTime);
    return hasMassEditModel ? massEditLoaded && overTimeLoaded : overTimeLoaded;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectTabbedPricingMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

/**
 * Retrieve the viewDataStates to be included in the tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingViewDataStates = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const viewDataStates = hasMassEditModel ? [viewDataStateOverTime, viewDataStateMassEdit] : [viewDataStateOverTime];
    return viewDataStates;
  },
);

//#endregion
